import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  Link,
  Paper,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Phone,
  Facebook,
  Close,
  BadgeOutlined,
  CheckCircle,
  Check,
  Error as ErrorIcon,
  Send,
} from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { validateEmail, validateTelefonoChileno, validateRUT, formatRUT } from '../../utils/validations';
import api from '../../services/api';

// Reutilizar los estilos del LoginModal
const RegisterContainer = styled(Paper)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(5px)',
});

const RegisterCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: '500px',
  width: '90%',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
});

const LogoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
  position: 'relative',
  top: '-20px',
});

const LogoImage = styled('img')({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  border: '3px solid #2e5a33',
  backgroundColor: '#fff',
  padding: '3px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
});

const StyledTextField = styled(TextField)({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#2e5a33',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2e5a33',
    },
  },
});

const RegisterButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '12px',
  padding: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

const SocialButton = styled(Button)({
  borderRadius: '12px',
  padding: '10px',
  textTransform: 'none',
  fontWeight: 500,
  border: '2px solid #e0e0e0',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    transform: 'translateY(-1px)',
  },
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '15px',
  right: '15px',
  color: '#666',
  '&:hover': {
    color: '#2e5a33',
    backgroundColor: 'rgba(46, 90, 51, 0.1)',
  },
});

const VerificationCodeInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    '& input': {
      textAlign: 'center',
      fontSize: '24px',
      fontWeight: 'bold',
      letterSpacing: '8px',
    },
  },
});

interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const RegisterModal: React.FC<RegisterModalProps> = ({ open, onClose, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '+56 ',
    rut: '',
    password: '',
    confirmPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Estados para verificación de email
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'form' | 'verify' | 'success'>('form');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { register, loginWithGoogle } = useAuth();

  // Verificar disponibilidad del email en tiempo real
  const checkEmailAvailability = useCallback(async (email: string) => {
    const emailError = validateEmail(email);
    if (emailError) {
      setEmailStatus('idle');
      return;
    }
    
    setEmailStatus('checking');
    try {
      const response = await api.get(`/auth/check-email/?email=${encodeURIComponent(email)}`);
      setEmailStatus(response.data.available ? 'available' : 'taken');
      if (!response.data.available) {
        setErrors(prev => ({
          ...prev,
          email: 'Este correo ya está registrado'
        }));
      }
    } catch (error) {
      setEmailStatus('idle');
    }
  }, []);

  // Debounce para verificación de email
  useEffect(() => {
    const emailError = validateEmail(formData.email);
    if (formData.email && !emailError) {
      const timer = setTimeout(() => {
        checkEmailAvailability(formData.email);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setEmailStatus('idle');
    }
  }, [formData.email, checkEmailAvailability]);

  // Countdown para reenvío de código
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    // Para el campo teléfono, asegurar que siempre empiece con +56
    if (field === 'telefono') {
      if (!value.startsWith('+56')) {
        value = '+56 ' + value.replace(/^\+?56\s?/, '');
      }
      // Evitar que se borre el prefijo +56
      if (value.length < 4) {
        value = '+56 ';
      }
    }
    
    // Para el campo RUT, formatear automáticamente
    if (field === 'rut') {
      value = formatRUT(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo al escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Reset email verification si cambia el email
    if (field === 'email') {
      setEmailVerified(false);
    }
    
    // Validación en tiempo real para confirmación de contraseña
    if (field === 'confirmPassword') {
      if (value && formData.password && value !== formData.password) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: 'Las contraseñas no coinciden'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
    
    // Validación en tiempo real cuando se cambia la contraseña principal
    if (field === 'password') {
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: 'Las contraseñas no coinciden'
        }));
      } else if (formData.confirmPassword && value === formData.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validar nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    // Validar apellido
    if (!formData.apellido.trim()) {
      newErrors.apellido = 'El apellido es requerido';
    }

    // Validar email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    } else if (emailStatus === 'taken') {
      newErrors.email = 'Este correo ya está registrado';
    }

    // Validar teléfono
    const phoneError = validateTelefonoChileno(formData.telefono);
    if (phoneError) {
      newErrors.telefono = phoneError;
    }

    // Validar RUT
    if (!formData.rut.trim()) {
      newErrors.rut = 'El RUT es requerido';
    } else if (!validateRUT(formData.rut)) {
      newErrors.rut = 'RUT inválido';
    }

    // Validar contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validar confirmación de contraseña
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debes confirmar tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendVerificationCode = async () => {
    if (!validateForm()) {
      return;
    }

    setSendingCode(true);
    setErrors({});
    
    try {
      console.log('Enviando código de verificación a:', formData.email);
      // Agregar timeout de 30 segundos para evitar que se quede colgado
      const response = await api.post('/auth/send-verification-code/', 
        { email: formData.email },
        { timeout: 30000 }
      );
      console.log('Respuesta:', response.data);
      setVerificationStep('verify');
      setCountdown(60); // 60 segundos para reenviar
    } catch (err: any) {
      console.error('Error al enviar código:', err);
      console.error('Respuesta del servidor:', err.response?.data);
      
      // Si es timeout o error de red, ofrecer continuar sin verificación
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || !err.response) {
        setErrors({ 
          general: 'El servidor de correo está tardando mucho. ¿Deseas continuar sin verificar el email? El código se enviará en segundo plano.' 
        });
      } else if (err.response?.data?.error?.includes('ya está registrado')) {
        setErrors({ 
          email: 'Este correo electrónico ya está registrado. ¿Ya tienes una cuenta? Intenta iniciar sesión.' 
        });
      } else {
        const errorMessage = err.response?.data?.error || err.message || 'Error al enviar el código. Intenta de nuevo.';
        setErrors({ general: errorMessage });
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setErrors({ code: 'Ingresa el código de 6 dígitos' });
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await api.post('/auth/verify-code/', {
        email: formData.email,
        codigo: verificationCode
      });
      
      if (response.data.valid) {
        setEmailVerified(true);
        // Proceder con el registro
        await completeRegistration();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Código inválido';
      setErrors({ code: errorMessage });
    } finally {
      setVerifyingCode(false);
    }
  };

  const completeRegistration = async () => {
    setLoading(true);
    try {
      await register({
        username: formData.email,
        email: formData.email,
        password: formData.password,
        password2: formData.confirmPassword,
        first_name: formData.nombre,
        last_name: formData.apellido,
        telefono: formData.telefono,
        rut: formData.rut,
        rol: 'cliente',
      });
      // Mostrar pantalla de éxito
      setVerificationStep('success');
      // Cerrar después de 3 segundos
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err: any) {
      console.error('Error en registro:', err);
      const errorMessage = err.message || 'Error al crear la cuenta';
      setErrors({ general: errorMessage });
      setVerificationStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formulario primero
    if (!validateForm()) {
      return;
    }
    
    // Enviar código de verificación al email ANTES de registrar
    await handleSendVerificationCode();
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    await handleSendVerificationCode();
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    setErrors({});
    
    try {
      if (credentialResponse.credential) {
        await loginWithGoogle(credentialResponse.credential);
        onClose();
      } else {
        setErrors({ general: 'No se recibió el token de Google' });
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Error al registrar con Google';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErrors({ general: 'Error al registrar con Google. Por favor intenta de nuevo.' });
  };

  const getEmailAdornment = () => {
    if (emailStatus === 'checking') {
      return <CircularProgress size={20} />;
    }
    if (emailStatus === 'available') {
      return <CheckCircle sx={{ color: '#4caf50' }} />;
    }
    if (emailStatus === 'taken') {
      return <ErrorIcon sx={{ color: '#f44336' }} />;
    }
    return null;
  };

  if (!open) return null;

  // Vista de verificación de código
  if (verificationStep === 'verify') {
    return (
      <RegisterContainer onClick={(e) => e.target === e.currentTarget && onClose()}>
        <RegisterCard>
          <CloseButton onClick={onClose}>
            <Close />
          </CloseButton>
          
          <CardContent sx={{ padding: '40px', paddingTop: '30px' }}>
            <LogoContainer>
              <LogoImage
                src="/assets/logos/logo-el-pinon-nuevo.png"
                alt="El Piñón - Hacienda Las Araucarias"
              />
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: '#2e5a33',
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 700,
                  marginTop: '15px',
                  textAlign: 'center',
                }}
              >
                Verifica tu Email
              </Typography>
            </LogoContainer>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Send sx={{ fontSize: 60, color: '#2e5a33', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Enviamos un código de 6 dígitos a:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
                {formData.email}
              </Typography>
            </Box>

            {errors.general && (
              <Alert severity="error" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
                {errors.general}
              </Alert>
            )}

            {errors.code && (
              <Alert severity="error" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
                {errors.code}
              </Alert>
            )}

            <VerificationCodeInput
              fullWidth
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
                setErrors({ ...errors, code: '' });
              }}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6 }}
            />

            <RegisterButton
              fullWidth
              variant="contained"
              onClick={handleVerifyCode}
              disabled={verifyingCode || verificationCode.length !== 6}
              sx={{ mb: 2 }}
            >
              {verifyingCode ? 'Verificando...' : 'Verificar y Crear Cuenta'}
            </RegisterButton>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                ¿No recibiste el código?{' '}
                {countdown > 0 ? (
                  <span>Reenviar en {countdown}s</span>
                ) : (
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleResendCode();
                    }}
                    sx={{ color: '#2e5a33', fontWeight: 600 }}
                  >
                    Reenviar código
                  </Link>
                )}
              </Typography>
              
              <Button
                onClick={() => setVerificationStep('form')}
                sx={{ mt: 2, color: '#666' }}
              >
                ← Volver al formulario
              </Button>
            </Box>
          </CardContent>
        </RegisterCard>
      </RegisterContainer>
    );
  }

  // Vista de éxito - registro completado
  if (verificationStep === 'success') {
    return (
      <RegisterContainer onClick={(e) => e.target === e.currentTarget && onClose()}>
        <RegisterCard>
          <CardContent sx={{ padding: '40px', textAlign: 'center' }}>
            <LogoContainer>
              <LogoImage
                src="/assets/logos/logo-el-pinon-nuevo.png"
                alt="El Piñón - Hacienda Las Araucarias"
              />
            </LogoContainer>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2 
            }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <Check sx={{ fontSize: 50, color: '#2e5a33' }} />
              </Box>
              
              <Typography
                variant="h4"
                sx={{
                  color: '#2e5a33',
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 700,
                }}
              >
                ¡Bienvenido/a!
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#4a7c59' }}>
                Tu cuenta ha sido creada exitosamente
              </Typography>
              
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                Ya puedes comenzar a comprar en El Piñón
              </Typography>
              
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 3, 
                  borderRadius: '12px',
                  width: '100%' 
                }}
              >
                Has iniciado sesión automáticamente. Redirigiendo...
              </Alert>
              
              <CircularProgress size={24} sx={{ mt: 2, color: '#2e5a33' }} />
            </Box>
          </CardContent>
        </RegisterCard>
      </RegisterContainer>
    );
  }

  return (
    <RegisterContainer onClick={(e) => e.target === e.currentTarget && onClose()}>
      <RegisterCard>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
        
        <CardContent sx={{ padding: '40px', paddingTop: '30px' }}>
          <LogoContainer>
            <LogoImage
              src="/assets/logos/logo-el-pinon-nuevo.png"
              alt="El Piñón - Hacienda Las Araucarias"
            />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: '#2e5a33',
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                marginTop: '15px',
                textAlign: 'center',
              }}
            >
              Crear Cuenta
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                textAlign: 'center',
                marginTop: '5px',
              }}
            >
              Únete a la familia El Piñón
            </Typography>
          </LogoContainer>

          {errors.general && (
            <Alert severity="error" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
              {errors.general}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <StyledTextField
                fullWidth
                label="Nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                error={!!errors.nombre}
                helperText={errors.nombre}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                fullWidth
                label="Apellido"
                value={formData.apellido}
                onChange={(e) => handleInputChange('apellido', e.target.value)}
                error={!!errors.apellido}
                helperText={errors.apellido}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <StyledTextField
              fullWidth
              type="email"
              label="Correo electrónico"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email || emailStatus === 'taken'}
              helperText={
                errors.email || 
                (emailStatus === 'taken' ? 'Este correo ya está registrado' : '') ||
                (emailStatus === 'available' ? '✓ Email disponible' : '')
              }
              FormHelperTextProps={{
                sx: { color: emailStatus === 'available' ? '#4caf50' : undefined }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {getEmailAdornment()}
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="Teléfono"
              placeholder="+56 9 1234 5678"
              value={formData.telefono}
              onChange={(e) => handleInputChange('telefono', e.target.value)}
              error={!!errors.telefono}
              helperText={errors.telefono}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="RUT"
              placeholder="12.345.678-9"
              value={formData.rut}
              onChange={(e) => handleInputChange('rut', e.target.value)}
              error={!!errors.rut}
              helperText={errors.rut}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlined sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Contraseña"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirmar contraseña"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <RegisterButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || sendingCode || emailStatus === 'taken'}
              sx={{ marginBottom: '20px' }}
            >
              {sendingCode ? 'Enviando código...' : 'Continuar'}
            </RegisterButton>
          </form>

          <Divider sx={{ marginBottom: '20px', color: '#ccc' }}>
            <Typography variant="body2" color="textSecondary">
              o regístrate con
            </Typography>
          </Divider>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '12px', 
            marginBottom: '20px',
            '& .nsm7Bb-HzV7m-LgbsSe': {
              width: '100% !important',
              justifyContent: 'center !important'
            }
          }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signup_with"
              shape="rectangular"
              width="400"
              logo_alignment="left"
            />
            <SocialButton
              fullWidth
              startIcon={<Facebook />}
              disabled={true}
            >
              Facebook
            </SocialButton>
          </Box>

          <Box
            sx={{
              textAlign: 'center',
              paddingTop: '20px',
              borderTop: '1px solid #e0e0e0',
              color: '#666',
              fontSize: '14px',
            }}
          >
            ¿Ya tienes cuenta?{' '}
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onSwitchToLogin();
              }}
              sx={{
                color: '#2e5a33',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Inicia sesión aquí
            </Link>
          </Box>
        </CardContent>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterModal;
